/* شارة التقسيط على صفحة المنتج — ترفع التحويل بعرض قيمة الدفعة.
   تظهر فقط عندما يكون مزوّد تقسيط مفعّلاً ومتاحاً لدولة المتصفح الحالية. */
import React, { useEffect, useState } from "react";
import http from "@/api";
import { useApp } from "@/context";
import TabbyPromo from "@/components/TabbyPromo";

/* حدود التقسيط لكل دولة — تُجلب مرة واحدة وتُشارك بين كل بطاقات المنتجات */
const _bnplLimitsCache = {};
export function useBnplHint(country) {
  const [limits, setLimits] = useState(_bnplLimitsCache[country] || null);
  useEffect(() => {
    if (!country) return undefined;
    if (_bnplLimitsCache[country]) { setLimits(_bnplLimitsCache[country]); return undefined; }
    let on = true;
    http.get(`/bnpl/availability?country=${country}&amount=1000`)
      .then(({ data }) => {
        const info = data?.paylater || data?.tabby || data?.tamara || null;
        const provider = data?.paylater ? "PayLater" : data?.tabby ? "tabby" : data?.tamara ? "tamara" : null;
        const out = provider && info
          ? { provider, min: Number(info.min) || 300, max: Number(info.max) || 25000 }
          : { provider: null };
        _bnplLimitsCache[country] = out;
        if (on) setLimits(out);
      })
      .catch(() => {});
    return () => { on = false; };
  }, [country]);
  return limits;
}

/* كاش إعدادات الضريبة — يُجلب مرة واحدة ويُشارك بين كل الشارات */
let _taxCfgCache = null;
let _taxCfgPromise = null;
function useTaxConf(country) {
  const [cfg, setCfg] = useState(_taxCfgCache);
  useEffect(() => {
    if (_taxCfgCache) { setCfg(_taxCfgCache); return undefined; }
    let on = true;
    _taxCfgPromise = _taxCfgPromise || http.get("/tax-config").then((r) => {
      _taxCfgCache = r.data || {};
      return _taxCfgCache;
    }).catch(() => ({}));
    _taxCfgPromise.then((c) => { if (on) setCfg(c); });
    return () => { on = false; };
  }, [country]);
  return (cfg && cfg[String(country || "").toUpperCase()]) || null;
}

export function BnplBadge({ price, source = "product", includesVat = false }) {
  const { country, lang, formatPrice } = useApp();
  const [avail, setAvail] = useState(null);
  const taxConf = useTaxConf(country);

  // 🧾 متطلب تابي: السنيبت يستقبل السعر الذي يدفعه العميل فعلاً (شامل الضريبة) —
  // نفس أساس السلة والدفع، حتى يتطابق القسط المعروض في كل الصفحات
  const cc = String(country || "").toUpperCase();
  const vatRate = taxConf != null ? (Number(taxConf.vat_rate) || 0) : (cc === "SA" ? 0.15 : 0);
  const payable = (() => {
    if (includesVat || !(vatRate > 0) || taxConf?.prices_include_vat) {
      return Math.round(Number(price || 0) * 100) / 100;
    }
    const g = Math.round(Number(price || 0) * (1 + vatRate) * 100) / 100;
    return taxConf?.rounding === "integer" ? Math.round(g) : g;
  })();

  useEffect(() => {
    let on = true;
    setAvail(null);
    if (!country || !payable) return undefined;
    const t = setTimeout(() => {
      http.get(`/bnpl/availability?country=${country}&amount=${payable}`)
        .then(({ data }) => { if (on) setAvail(data); })
        .catch(() => {});
    }, 200);
    return () => { on = false; clearTimeout(t); };
  }, [country, payable]);

  if (!avail || !payable) return null;
  const ar = lang === "ar";
  const quarter = formatPrice(payable / 4);

  if (avail.tabby?.available || avail.tamara?.available) {
    const tmIns = avail.tamara?.instalments || [];
    const tmN = tmIns.length ? Math.max(...tmIns) : null;
    return (
      <div className="mt-2.5 space-y-1.5" data-testid="bnpl-badge-sa">
        {avail.tabby?.available && (
          /* سنيبت Tabby الرسمي — السعر شامل الضريبة، تابي تعرض الخطط (لا حساب يدوي) */
          <TabbyPromo price={payable} lang={lang} source={source} className="" />
        )}
        {avail.tamara?.available && (
          <div className="flex items-center flex-wrap gap-2 rounded-xl px-3 py-2"
               style={{ background: "rgba(228,255,85,0.22)", border: "1px solid rgba(190,214,40,0.6)" }}
               data-testid="bnpl-badge-tamara">
            <span className="text-[13px] font-black">tamara</span>
            <span className="text-[12px] font-bold">
              {tmN
                ? (ar ? `${tmN} أقساط شهرية من ${formatPrice(payable / tmN)} — بدون رسوم تأخير`
                      : `${tmN} monthly payments of ${formatPrice(payable / tmN)} — no late fees`)
                : (ar ? "دفعات شهرية مع تمارا — بدون رسوم تأخير" : "Monthly payments with Tamara — no late fees")}
            </span>
          </div>
        )}
      </div>
    );
  }
  if (avail.paylater?.available) {
    return (
      <div className="mt-2.5 flex items-center flex-wrap gap-2 rounded-xl px-3 py-2"
           style={{ background: "rgba(11,42,91,0.07)", border: "1px solid rgba(11,42,91,0.25)" }}
           data-testid="bnpl-badge-qa">
        <span className="text-[13px] font-black" style={{ color: "#0B2A5B" }}>PayLater</span>
        <span className="text-[12px] font-bold">
          {ar ? `قسّطها على 4 دفعات من ${quarter} — بدون فوائد` : `Split in 4 payments of ${quarter} — interest-free`}
        </span>
      </div>
    );
  }
  return null;
}
